import _isNil from "lodash/isNil";
import _identity from "lodash/identity";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import numeral from 'numeral';
import { PRODUCT_TYPES } from '../../constants';
import { callSelectorByProductType, getLoanAmountMax, getLoanAmountMin, getLoanTermMax, getLoanTermMin } from '../../selectors/common';
import { getAvailableTermLengths as getEduAvailableTermLengths } from '../../selectors/education-loans';
/**
 * Get formatted available term lengths
 *
 * @param   {Object} marketplaceEntity  The "loans" marketplaceEntity
 * @return  {string}                    The formatted range
 */

export var getFormattedTermRange = function getFormattedTermRange(marketplaceEntity) {
  var MONTHS_IN_YEAR = 12;

  var pluralize = function pluralize(num, str) {
    return "".concat(num, " ").concat(str).concat(num > 1 ? 's' : '');
  };

  var convertToYears = function convertToYears(mo) {
    var roundFunc = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : _identity;
    return roundFunc(mo / MONTHS_IN_YEAR);
  };

  var max = getLoanTermMax(marketplaceEntity);
  var min = getLoanTermMin(marketplaceEntity);
  var hasMax = !_isNil(max);
  var hasMin = !_isNil(min);
  var maxInYears = convertToYears(max);
  var minInYears = convertToYears(min); // only convert to years if can divide cleanly

  var upgradeMaxToYears = max >= MONTHS_IN_YEAR && max % MONTHS_IN_YEAR === 0;
  var upgradeMinToYears = min >= MONTHS_IN_YEAR && min % MONTHS_IN_YEAR === 0; // permutations of semantic combinations

  if (hasMin && hasMax && upgradeMinToYears && upgradeMaxToYears) {
    // both exist, both in years
    return "".concat(minInYears, " to ").concat(maxInYears, " years");
  }

  if (hasMin && hasMax && upgradeMaxToYears) {
    // both exist, only max in years
    return "".concat(pluralize(min, 'month'), " to ").concat(pluralize(maxInYears, 'year'));
  }

  if (hasMin && hasMax) {
    // both exist, both in months
    return "".concat(min, " to ").concat(max, " months");
  }

  if (hasMin && upgradeMinToYears) {
    // only min exists & in years
    return pluralize(minInYears, 'year');
  }

  if (hasMin) {
    // only min exists & in months
    return pluralize(min, 'month');
  }

  if (hasMax && upgradeMaxToYears) {
    // only max exists & in years
    return pluralize(maxInYears, 'year');
  }

  if (hasMax) {
    // only max exists & in months
    return pluralize(max, 'month');
  }

  return undefined;
};
/**
 * Gets the available term lengths in formatted range
 * @note EDU leverages a marketplace selector `getAvailableTermLengths`
 *
 * @param   {Object} marketplaceEntity  The "loans" marketplace entity
 * @return  {String}                    The available term lengths
 */

export var getAvailableTermLengths = function getAvailableTermLengths(marketplaceEntity) {
  return callSelectorByProductType(marketplaceEntity, _defineProperty({}, PRODUCT_TYPES.EDUCATION_LOANS, getEduAvailableTermLengths), function () {
    return getFormattedTermRange(marketplaceEntity);
  });
};
/**
 * Gets possible loan amounts in a formatted range
 *
 * @param   {Object} marketplaceEntity  The "loans" marketplace entity
 * @return  {String}                    The loan amount range
 */

export var getLoanAmountRange = function getLoanAmountRange(marketplaceEntity) {
  var formatter = function formatter(value) {
    return numeral(value).format('0,0');
  };

  var max = getLoanAmountMax(marketplaceEntity);
  var min = getLoanAmountMin(marketplaceEntity);
  var hasMax = !_isNil(max);
  var hasMin = !_isNil(min);
  var formattedMax = formatter(max);
  var formattedMin = formatter(min);

  if (hasMin && hasMax) {
    return "$".concat(formattedMin, " - $").concat(formattedMax);
  }

  if (hasMin) {
    return "$".concat(formattedMin);
  }

  if (hasMax) {
    return "$".concat(formattedMax);
  }

  return undefined;
};